/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { createGlobalStyle } from 'styled-components';

import { staticURL } from 'core/url';
import { Link, ExternalLink } from 'core/routing';

import { Button, Heading, Paragraph, Loading } from 'components';

import { Date } from 'apps/components/date';
import { conf as mainConf } from 'apps/config.yaml';

import { ViralResponseLandingManager } from 'apps/microSite/pages/customLanding/viralResponse/appManager';

import { observer } from 'mobx-react';
import { imageSrc } from 'apps/file/utils';

import MontserratRegularWoff2 from 'assets/fonts/montserrat-regular.woff2';
import MontserratRegularWoff from 'assets/fonts/montserrat-regular.woff';
import MontserratSemiboldWoff2 from 'assets/fonts/montserrat-semibold.woff2';
import MontserratSemiboldWoff from 'assets/fonts/montserrat-semibold.woff';
import MontserratExtraBoldWoff2 from 'assets/fonts/montserrat-extraBold.woff2';
import MontserratExtraBoldWoff from 'assets/fonts/montserrat-extraBold.woff';

import { ContentLink } from '../../../../utils';
import {
  ViralResponseLandingStyled,
  HeadingStyled,
  HeadingExtra,
  HeadingExtraInline,
  ButtonStyled,
  SmallRotated,
  Hero,
  Info,
  FigureCollage,
  ImgCollage,
  TextCollage,
  Collage,
  NavSocial,
  IconStyled,
  ArrowImage,
  LandingSection,
  ButtonSectionStyled,
  Projects,
  Project,
  LandingSectionContent,
  Issue,
  CommentersStyled,
  Avatar,
  Story,
  ParagraphStory,
  SlackLogo,
} from './styles';

const GlobalStyle = createGlobalStyle`
  /* Montserrat Regular */
  @font-face {
    font-family: Montserrat;
    font-display: auto;
    src: local('Montserrat Regular'), local('Montserrat-Regular'), url("${MontserratRegularWoff2}") format("woff2"),
        url("${MontserratRegularWoff}") format("woff");
    font-weight: 400;
    font-style: normal;
  }
  /* Montserrat SemiBold */
  @font-face {
    font-family: Montserrat;
    font-display: auto;
    src: local('Montserrat SemiBold'), local('Montserrat-SemiBold'), url("${MontserratSemiboldWoff2}") format("woff2"),
        url("${MontserratSemiboldWoff}") format("woff");
    font-weight: 600;
    font-style: normal;
  }
  /* Montserrat ExtraBold */
  @font-face {
    font-family: Montserrat;
    font-display: auto;
    src: local('Montserrat ExtraBold'), local('Montserrat-ExtraBold'), url("${MontserratExtraBoldWoff2}") format("woff2"),
        url("${MontserratExtraBoldWoff}") format("woff");
    font-weight: 800;
    font-style: normal;
  }
`;

@observer
export class ViralResponseLanding extends React.Component {
  constructor(props) {
    super(props);
    this.landingManager = new ViralResponseLandingManager(null, props);
  }

  render() {
    const { projectsConn, issuesConn, storiesConn } = this.landingManager;
    const projects = projectsConn.arrayApp;
    const issues = issuesConn.arrayApp;
    const stories = storiesConn.arrayApp;
    return (
      <>
        <GlobalStyle />
        <ViralResponseLandingStyled>
          <Hero>
            <Info>
              <HeadingExtra>
                Ingenuity can’t <br /> be locked down.
              </HeadingExtra>
              <HeadingStyled
                text='Use yours against COVID here'
                level={2}
                size='medium'
              />
              <SmallRotated>Welcome to Viral Response</SmallRotated>
              <Paragraph>
                A global crisis demands a global response. Innovating without
                borders or barriers to make a bigger difference together.
              </Paragraph>
              <Paragraph>
                Welcome to Viral Response. A dedicated platform to support
                COVID-19 open hardware innovation
                <br />
                <strong>- powered by Wikifactory.</strong>
              </Paragraph>
              <Link name='home.signUp'>
                <ButtonStyled text='Join Viral Response' />
              </Link>
            </Info>
            <Collage>
              <FigureCollage>
                <ImgCollage
                  src={staticURL(
                    '/assets/whitelabels/viralresponse/collage.jpg',
                  )}
                  alt={
                    "We're a community of makers, designers, engineers, experts and creative problem solvers"
                  }
                />
                <NavSocial>
                  <li>
                    <ExternalLink to='https://www.facebook.com/wikifactory'>
                      <IconStyled name='facebook' size='medium' />
                    </ExternalLink>
                  </li>
                  <li>
                    <ExternalLink to='https://twitter.com/wikifactory'>
                      <IconStyled name='twitter' size='medium' />
                    </ExternalLink>
                  </li>
                  <li>
                    <ExternalLink to='https://www.instagram.com/wikifactoryhq/'>
                      <IconStyled name='instagram' size='medium' />
                    </ExternalLink>
                  </li>
                  <li>
                    <ExternalLink to='https://join.slack.com/t/wikifactory-community/shared_invite/zt-ej6upi7p-t0NgC15HZt~qXq6YhhCu7Q'>
                      <IconStyled name='slack' size='medium' />
                    </ExternalLink>
                  </li>
                </NavSocial>
              </FigureCollage>
              <TextCollage>
                <Paragraph>
                  We're a community of makers, designers, engineers, experts and
                  creative problem solvers who{' '}
                  <strong>innovate globally, and produce locally</strong>.
                  Overcoming existing supply chains and intellectual property
                  restrictions, to get the right products in{' '}
                  <strong>the right hands, wherever they're needed</strong>.
                </Paragraph>
                <Paragraph>
                  Here you'll find{' '}
                  <strong>the information, tools and connections</strong> to
                  help make your contributions count. By solving an urgent need
                  today, we can build a{' '}
                  <strong>better way of making for the future</strong>.
                </Paragraph>
              </TextCollage>

              <ArrowImage
                src={staticURL(
                  '/assets/whitelabels/viralresponse/orange-line.svg',
                )}
                aria-hidden='true'
              />
            </Collage>
          </Hero>
          <LandingSection projects>
            <HeadingExtra level={3}>Making it happen</HeadingExtra>
            <Paragraph>
              Start or contribute to a project, with free access to easy-to-use,
              next-generation <br /> product development, workflow and
              visualisation tools.
            </Paragraph>
            <LandingSectionContent>
              <Projects>
                {projects && !!projects.length ? (
                  projects.map(project => {
                    let spaceType = 'profile';
                    if (project.spacePath[0] === '+') {
                      spaceType = 'initiative';
                    }
                    return (
                      <Project key={project.id}>
                        <Link
                          name={`${spaceType}.project`}
                          params={{
                            [spaceType]: project.spacePath,
                            slug: project.slug,
                          }}
                          content={project.content}
                        >
                          <img
                            src={project.image.src}
                            alt={project.descriptionSnippet}
                          />
                          <figcaption>{project.name}</figcaption>
                        </Link>
                      </Project>
                    );
                  })
                ) : (
                  <Loading isLayoutCentered text='Loading projects' />
                )}
              </Projects>
            </LandingSectionContent>
            <Link name='home.contribute'>
              <ButtonSectionStyled text='See all projects' />
            </Link>
          </LandingSection>
          <LandingSection textAlign='right' issues>
            <HeadingExtra level={3}>Making a difference</HeadingExtra>
            <Paragraph>
              Collaborate as teams or individually to accelerate vital products
              to help beat COVID-19.
            </Paragraph>
            <LandingSectionContent>
              {issues && !!issues.length ? (
                issues.map((issue, index) => {
                  const {
                    id,
                    title,
                    creatorProfile,
                    slug,
                    inSpace,
                    dateCreated,
                    comments,
                    commenters,
                    content,
                  } = issue;
                  const projectSlug = `${inSpace.parent.path} / ${inSpace.content.slug}`;
                  const spacePath = projectSlug.includes('@')
                    ? 'profile.project'
                    : 'initiative.project';
                  const spaceParams =
                    spacePath === 'profile.project'
                      ? {
                          profile: inSpace.parent.path,
                          slug: inSpace.content.slug,
                        }
                      : {
                          initiative: inSpace.parent.path,
                          slug: inSpace.content.slug,
                        };
                  return (
                    <Issue key={id} squaresInvert={index % 2 === 0}>
                      <div>
                        {/* FIXME: Replace this Avatar by new one refactored */}
                        <Link
                          name='profile'
                          params={{ profile: creatorProfile.username }}
                          content={creatorProfile.content}
                        >
                          <Avatar
                            src={imageSrc(
                              creatorProfile.avatar,
                              creatorProfile.imageFallbackPath,
                              mainConf.imgSizes.profile.small,
                            )}
                          />
                        </Link>
                        <header>
                          {/* FIXME: Replace container order once last version of Heading can be used here */}
                          <ContentLink
                            contentSubPath='issue'
                            contentObj={issue}
                            params={{
                              slug: inSpace.content.slug,
                              issueSlug: slug,
                            }}
                            content={content}
                          >
                            <Heading level={3} text={title} size='default' />
                          </ContentLink>
                          <small>
                            Opened in{' '}
                            <Link
                              name={spacePath}
                              params={spaceParams}
                              content={content}
                            >
                              <b>
                                {`+${inSpace.parent.content.slug}/${inSpace.content.slug}`}{' '}
                              </b>
                            </Link>
                            {' | '}
                            <Date value={dateCreated} />
                            {' | '}
                            <ContentLink
                              contentSubPath='issue'
                              contentObj={issue}
                              params={{
                                slug: inSpace.content.slug,
                                issueSlug: slug,
                              }}
                              content={content}
                            >
                              <b>
                                {comments.totalCount > 0
                                  ? `${comments.totalCount} Comments`
                                  : 'Discuss'}
                              </b>
                            </ContentLink>
                          </small>
                        </header>
                        {commenters && !!commenters.length && (
                          <CommentersStyled commenters={commenters} />
                        )}
                        <ContentLink
                          contentSubPath='issue'
                          contentObj={issue}
                          params={{
                            slug: inSpace.content.slug,
                            issueSlug: slug,
                          }}
                          content={content}
                        >
                          <Button icon='chat_multiple' variant='tertiary' />
                        </ContentLink>
                      </div>
                    </Issue>
                  );
                })
              ) : (
                <Loading isLayoutCentered text='Loading issues' />
              )}
            </LandingSectionContent>
            <Link name='home.contribute.issues'>
              <ButtonSectionStyled text='See all feedback requests' />
            </Link>
          </LandingSection>
          <LandingSection stories>
            <HeadingExtra level={3}>Making history</HeadingExtra>
            <Paragraph>
              Stories and announcements from a global community, working with
              one aim: <br /> making what’s needed to beat COVID-19.
            </Paragraph>
            <LandingSectionContent>
              {stories && !!stories.length ? (
                stories.map(
                  ({ id, title, avatar, slug, spacePath, content }, index) => {
                    // FIXME this is repeated in several places
                    let spaceType = 'profile';
                    if (spacePath[0] === '+') {
                      spaceType = 'initiative';
                    }
                    return (
                      <Link
                        name={`${spaceType}.story`}
                        params={{ [spaceType]: spacePath, slug }}
                        content={content}
                        key={id}
                      >
                        <Story squaresInvert={index % 2 === 0}>
                          <img src={avatar.url} alt={title} />
                          <figcaption>
                            <ParagraphStory text={title} v isTruncated />
                          </figcaption>
                        </Story>
                      </Link>
                    );
                  },
                )
              ) : (
                <Loading isLayoutCentered text='Loading stories' />
              )}
            </LandingSectionContent>
            <Link name='home.learn.stories'>
              <ButtonSectionStyled text='See all stories' />
            </Link>
          </LandingSection>
          <LandingSection textAlign='center' slack>
            <SlackLogo
              src={staticURL(
                '/assets/whitelabels/viralresponse/slack-mark.svg',
              )}
              alt='Slack mark logo'
            />
            <HeadingExtraInline level={3}>Join us on Slack</HeadingExtraInline>
            <LandingSectionContent>
              <Paragraph>
                Join our community conversation on the Viral Response slack
                channel.
              </Paragraph>
            </LandingSectionContent>

            <ExternalLink to='https://join.slack.com/t/wikifactory-community/shared_invite/zt-ej6upi7p-t0NgC15HZt~qXq6YhhCu7Q'>
              <ButtonSectionStyled text='Join the Slack channel' />
            </ExternalLink>
          </LandingSection>
        </ViralResponseLandingStyled>
      </>
    );
  }
}
